import moment from "moment";

import { formatTimestamp } from "utils/utils";
import { CHART_HEADER_ACTIONS } from "constants/chartConstant";

import CHART_ACTIONS from "../actionTypes/charts";

const NormalizationFilters = {
  Baseline: null,
  FeedFlowH: null,
  FeedFlowL: null,
  RecoveryH: null,
  RecoveryL: null,
  RejectConductivityH: null,
  RejectConductivityL: null
};

const tagDataInitialState = {
  TagsWithoutData: [],
  OrigPlotData: null,
  PlotData: null,
  PlotZoomData: {}
};

const chartTagsInitialState = {
  Alarms: [],
  OrignialAlarms: [],
  IsChartUpdating: true,
  Format: "",
  Duration: "",
  DurationRanges: {},
  ChartXAxisRange: [],
  Id: null,
  Tags: [],
  TagsToShow: [],
  NormalizedTags: [],
  EditOrignalTags: [],
  CurrentView: null,
  NormalizationFilters,
  TagDefaultConfig: { Type: "lines+markers" },
  TagsInDb: [],
  ...tagDataInitialState
};

const chartInitialState = {
  IsLoading: false,
  AllTags: [],
  TagOptions: [],
  AlarmsHistory: [],
  ...chartTagsInitialState
};

const chartData = (state = chartInitialState, action) => {
  switch (action.type) {
    case CHART_ACTIONS.CHART_DATA_SUCCESS:
      return setChartData(state, action.payload);

    case CHART_ACTIONS.SET_ALARMS_HISTORY:
      return setAlarmsHistory(state, action.payload);

    case CHART_ACTIONS.UPDATE_ALARMS:
      return onEditAlarms(state, action.payload);

    case CHART_ACTIONS.SET_CHART_LOADING: {
      const IsLoading = action.payload;
      return { ...state, IsLoading };
    }

    case CHART_ACTIONS.SET_CHART_TAG_DATA_LOADING: {
      const IsChartUpdating = action.payload;
      return { ...state, IsChartUpdating };
    }

    case CHART_ACTIONS.SET_CHART_TAGS: {
      const AllTags = action.payload;
      const TagOptions = getUnusedTags(AllTags, [...state.Tags]);
      {
        return { ...state, AllTags, TagOptions };
      }
    }

    case CHART_ACTIONS.UPDATE_TAGS_WITHOUT_DATA: {
      const arr = [...state.TagsWithoutData, ...action.payload];
      const TagsWithoutData = [...new Map(arr.map(item => [item.TagId, item])).values()];
      return { ...state, TagsWithoutData };
    }

    case CHART_ACTIONS.ADD_TAG_SUCCESS: {
      return onTagAdditions(state, action.payload);
    }

    case CHART_ACTIONS.ON_CHART_PLOT_ZOOM: {
      return onPlotZoom(state, action.payload);
    }

    case CHART_ACTIONS.SET_PLOT_DATA: {
      const { respData, isResetZoom } = action.payload;
      let chartState = state;
      if (isResetZoom) {
        chartState = onPlotZoom(state, { isReset: true });
      }
      const statePlotData = chartState.PlotData || {};
      const PlotData = { ...statePlotData, ...respData };
      return { ...chartState, PlotData };
    }

    case CHART_ACTIONS.UPDATE_PLOT_DATA: {
      let newChartState = { ...state };
      const { type, data } = action.payload;
      let newPlotData = { ...newChartState.PlotData };
      if (type === "ADD") {
        newChartState = onPlotZoom(state, { isReset: true });
        newPlotData = { ...newChartState.PlotData };
        const { Time, ...tagsData } = data;
        if (!Time.length || newPlotData?.Time?.length === Time.length) {
          newPlotData = { ...newPlotData, ...tagsData };
        } else {
          newPlotData = { ...newPlotData, ...data };
        }
      }
      return { ...newChartState, PlotData: newPlotData };
    }

    case CHART_ACTIONS.UPDATE_CHART_DURATION: {
      const Duration = action.payload;
      return { ...state, Duration };
    }

    case CHART_ACTIONS.SET_DURATION_RANGES: {
      const DurationRanges = action.payload;
      const ChartXAxisRange = [formatTimestamp(DurationRanges.startTime * 1000), formatTimestamp(DurationRanges.endTime * 1000)];
      const Alarms = filterAlarmsWitninRanges(DurationRanges, state.AlarmsHistory);
      return { ...state, DurationRanges, ChartXAxisRange, Alarms };
    }

    case CHART_ACTIONS.UPDATE_CHART_FORMAT: {
      const Format = action.payload;
      return { ...state, Format };
    }

    case CHART_ACTIONS.SET_CURRENT_EDITVIEW: {
      return { ...state, CurrentView: action.payload };
    }

    case CHART_ACTIONS.UPDATE_EDITED_TAGS:
      return onEditTags(state, action.payload);

    case CHART_ACTIONS.DELETE_TAG_SUCCESS:
      return onDeleteTags(state, action.payload);

    case CHART_ACTIONS.UPDATE_NORMALIZE_FILTERS: {
      const updatedFilters = action.payload;
      const NormalizationFilters = { ...state.NormalizationFilters, ...updatedFilters };
      return { ...state, NormalizationFilters };
    }

    case CHART_ACTIONS.RESET_CHART_DATA:
      return chartInitialState;

    case CHART_ACTIONS.RESET_TAGS_DATA:
      return { ...state, ...tagDataInitialState };

    case CHART_ACTIONS.RESET_CHART_TAGS_DATA:
      return { ...state, ...chartTagsInitialState };

    case CHART_ACTIONS.TAGS_LIMITS: {
      const TagsInDb = action.payload;
      return { ...state, TagsInDb };
    }
    default:
      return state;
  }
};

const onPlotZoom = (state, payload) => {
  let PlotZoomData = { ...state.PlotZoomData };
  let { PlotData, OrigPlotData, DurationRanges, ChartXAxisRange } = state;
  if (payload?.isReset && OrigPlotData) {
    PlotZoomData = {};
    PlotData = JSON.parse(JSON.stringify(OrigPlotData));
    OrigPlotData = null;
    ChartXAxisRange = [formatTimestamp(DurationRanges.startTime * 1000), formatTimestamp(DurationRanges.endTime * 1000)];
  } else if (payload?.xRange) {
    if (!OrigPlotData) {
      OrigPlotData = PlotData;
    }
    const { xRange } = payload;
    ChartXAxisRange = [formatTimestamp(xRange[0]), formatTimestamp(xRange[1])];
    PlotZoomData = { ...PlotZoomData, ...payload };
  }
  return { ...state, PlotZoomData, PlotData, OrigPlotData, ChartXAxisRange };
};

const onTagAdditions = (state, addedTag) => {
  const Tags = [...state.Tags, addedTag];
  let NormalizedTags = [...state.NormalizedTags];
  if (addedTag.isNormalizedTag) {
    NormalizedTags = [...NormalizedTags, addedTag];
  }
  const TagsToShow = addedTag.Show ? [...state.TagsToShow, addedTag] : state.TagsToShow;
  const TagOptions = getUnusedTags(state.AllTags, Tags);
  return { ...state, Tags, NormalizedTags, TagsToShow, TagOptions };
};

const onEditAlarms = (state, payload) => {
  const { isOpened, updatedAlarms, isClosedView } = payload;
  let { OrignialAlarms, Alarms, CurrentView } = state;
  if (isOpened) {
    OrignialAlarms = Alarms;
  } else {
    if (!updatedAlarms) {
      Alarms = OrignialAlarms;
      OrignialAlarms = [];
    } else {
      Alarms = updatedAlarms;
    }
  }
  if (isClosedView) CurrentView = null;
  return { ...state, OrignialAlarms, CurrentView, Alarms };
};

const onDeleteTags = (state, payload) => {
  const deleteTagId = payload;
  let deletedItem;
  let { NormalizedTags } = state;
  const Tags = state.Tags.filter(tag => {
    const isDeleted = tag.TagId === deleteTagId;
    if (isDeleted) deletedItem = tag;
    return !isDeleted;
  });
  const PlotData = { ...state.PlotData };
  delete PlotData[deleteTagId];
  if (deletedItem && deletedItem.isNormalizedTag) {
    NormalizedTags = NormalizedTags.filter(({ TagId }) => TagId !== deleteTagId);
  }
  const EditOrignalTags = state.EditOrignalTags.filter(({ TagId }) => TagId !== deleteTagId);
  const TagOptions = getUnusedTags(state.AllTags, Tags);
  const TagsWithoutData = state.TagsWithoutData.filter(tag => tag.TagId !== deleteTagId);
  const TagsToShow = state.TagsToShow.filter(tag => tag.TagId !== deleteTagId);

  return { ...state, Tags, TagOptions, NormalizedTags, EditOrignalTags, TagsWithoutData, TagsToShow, PlotData };
};

const onEditTags = (state, payload) => {
  const { isOpened, updatedTags } = payload;
  let { EditOrignalTags, Tags, TagsToShow, CurrentView } = state;
  if (isOpened) {
    EditOrignalTags = Tags;
  } else {
    if (!updatedTags) {
      Tags = EditOrignalTags;
      TagsToShow = Tags.filter(tag => tag.Show);
    }
    EditOrignalTags = [];
    CurrentView = null;
  }
  return { ...state, EditOrignalTags, Tags, TagsToShow, CurrentView };
};

const setChartData = (state, payload) => {
  const { Tags, ...restParams } = payload;
  let alarmsData = {};
  let IsChartUpdating = false;
  if (Object.keys(restParams).length) {
    const { NormalizedTags, Alarms: activeAlarms, DurationRanges, NormalizationFilters } = restParams;
    const AlarmsHistory = updateEnabledAlarms(state.AlarmsHistory, activeAlarms);
    let Alarms = [];
    if (DurationRanges) {
      Alarms = filterAlarmsWitninRanges(DurationRanges, AlarmsHistory);
    }
    alarmsData = { NormalizedTags, NormalizationFilters, Alarms, AlarmsHistory };
    IsChartUpdating = true;
  }
  const TagsToShow = Tags.filter(tag => tag.Show);
  const TagOptions = getUnusedTags(state.AllTags, Tags);
  return { ...state, ...payload, Tags, TagsToShow, TagOptions, IsChartUpdating, ...alarmsData };
};

const updateEnabledAlarms = (allAlarms, activeAlarms) => {
  const list = allAlarms.map(alarm => {
    const { AlarmId } = alarm;
    alarm.Enable = Boolean(activeAlarms?.find(id => id === AlarmId));
    return alarm;
  });
  return list;
};

const filterAlarmsWitninRanges = (DurationRanges, Alarms) => {
  const { startTime, endTime } = DurationRanges;
  let filterdList = [];
  if (startTime && endTime) {
    filterdList = Alarms.filter(({ TimeStamp }) => {
      const unixTime = moment(TimeStamp).unix();
      return startTime <= unixTime && unixTime <= endTime;
    });
  }
  return filterdList;
};

const setAlarmsHistory = (state, AlarmsHistory) => {
  const Alarms = filterAlarmsWitninRanges(state.DurationRanges, AlarmsHistory);
  let OrignialAlarms = [];
  if (state.CurrentView === CHART_HEADER_ACTIONS.EDIT_ALARMS) {
    OrignialAlarms = Alarms;
  }
  return { ...state, AlarmsHistory, Alarms, OrignialAlarms };
};

const getUnusedTags = (allTags, usedTags) => allTags.filter(tag => !usedTags.some(item => item.TagId === tag.TagId));

export default chartData;
